// Retur Vendor — daftar, rincian, pembatalan, pengajuan (1E-δ, layar terakhir)
//
// ⛔ KENAPA LAYAR INI BARU ADA SEKARANG. Empat endpoint `/purchasing/returns`
// sudah hijau sejak Fase 1E, dan fungsi API-nya sudah ada di
// `lib/purchasingApi.js` — yang nol ada cuma layarnya. Endpoint tanpa layar
// bukan setengah selesai: ia NOL BISA DIPAKAI staf gudang sama sekali, dan
// ketiadaannya nol menimbulkan galat di mana pun. Diukur 29 Agustus 2026.
//
// NOL KONSTANTA BISNIS. Daftar status, warnanya, akibatnya pada stok, dan
// wewenang pengguna ini SEMUANYA dari GET /purchasing/meta/returns —
// endpoint yang dibuat bersamaan dengan layar ini, karena retur sempat jadi
// satu-satunya sumber daya purchasing tanpa `meta`.
//
// ⚠ SATU HAL YANG PALING GAMPANG SALAH DI LAYAR INI: retur MENGURANGI STOK.
// Ia satu-satunya layar purchasing yang mengeluarkan barang dari gudang atas
// nama vendor, dan pembatalannya MENGEMBALIKAN stok itu. Karena itu tiap
// status di sini menyebut akibat stoknya, bukan sekadar menerjemahkan kode.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge, toneFromMeta } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Undo2, RefreshCw, AlertCircle, PackageX, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../../lib/format";
import {
  listReturns, getReturn, cancelReturn, createReturn, getReturnsMeta,
  listGRNs, getGRN, apiErrorMessage,
} from "../../lib/purchasingApi";
import PenyelesaianRetur from "./PenyelesaianRetur";
import SisaUangKembaliPanel from "./SisaUangKembaliPanel";
import UangLebihPemasokPanel from "./UangLebihPemasokPanel";
import { useAuth } from "../../contexts/AuthContext";
import { SISA_UANG_KEMBALI_ROLES, UANG_LEBIH_ROLES } from "../../lib/roles";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariGrn, labelGrn, ketGrn } from "../../lib/sumberPilihan";

//: Cermin `URUT_SAH_RETUR` di backend/services/vendor_return_service.py.
const URUT_SAH = ["return_date", "return_number", "status", "created_at"];

const KOLOM_CSV = [
  { kunci: "return_number", judul: "Nomor" },
  { kunci: "return_date", judul: "Tanggal", ambil: (r) => formatDate(r.return_date) },
  { kunci: "vendor_name", judul: "Vendor" },
  { kunci: "po_number", judul: "PO" },
  { kunci: "reason", judul: "Alasan" },
  { kunci: "status", judul: "Status" },
];

/** Lencana status retur. Tone dikirim backend; terjemahan tone → warna hidup
 *  di satu tempat (StatusBadge), sama seperti lima layar purchasing lain. */
function statusBadge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

//: Ambang panjang alasan pembatalan. Backend menuntut min. 3 karakter
//: (`ReturnReasonBody`). Diperiksa di sini SUPAYA PENGGUNA TAHU SEBELUM
//: menekan tombol — bukan sebagai pengganti pagar backend.
const MIN_ALASAN = 3;

// POLA DAFTAR SERAGAM (27 Sep 2026; `docs/POLA_DAFTAR.md`) — layar acuan purchasing.
// ⛔ Dulu: `muat` bergantung pada `meta`, jadi daftar dipanggil DUA KALI tiap
//    membuka layar (sekali sebelum meta tiba, sekali sesudahnya), dan keduanya
//    berhenti di 50 baris pertama yang lalu disaring di browser — retur ke-51 dst.
//    tidak pernah bisa ditemukan. Sekarang: meta sekali, daftar per halaman dengan
//    cari/urut/saringan di server.
export default function VendorReturns() {
  const { roleCode } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [batalFor, setBatalFor] = useState(null);
  const [buatOpen, setBuatOpen] = useState(false);
  const [grnPilih, setGrnPilih] = useState(null);
  const [qty, setQty] = useState({});
  const [alasanBuat, setAlasanBuat] = useState("");
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);

  const daftar = useDaftar(rows, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "return_date", arah: "desc" },
    saringan: {
      status: {
        label: "Status",
        tampil: (v) => (meta?.statuses || []).find((x) => x.code === v)?.label || v,
      },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await listReturns(bangunParams({ offset: off, limit: n }));
    return r.data;
  }, [bangunParams]);

  // Meta SEKALI — terpisah dari daftar, supaya tibanya meta tidak memicu muat ulang.
  useEffect(() => {
    getReturnsMeta().then((m) => setMeta(m.data)).catch((e) => setErr(apiErrorMessage(e)));
  }, []);

  const muat = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await listReturns(bangunParams());
      setRows(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : (r.data?.items || []).length);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { muat(); }, [muat]);

  const bukaDetail = async (rid) => {
    setDetailLoading(true);
    try {
      const { data } = await getReturn(rid);
      setDetail(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const jalankanBatal = async () => {
    if (alasan.trim().length < MIN_ALASAN) return;
    setBusy(true);
    try {
      await cancelReturn(batalFor.id, { reason: alasan.trim() });
      toast.success("Retur dibatalkan — stok dikembalikan.");
      setBatalFor(null); setAlasan(""); setDetail(null);
      await muat();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const bukaBuat = () => {
    setBuatOpen(true); setGrnPilih(null); setQty({}); setAlasanBuat("");
  };

  const pilihGrn = async (gid) => {
    if (!gid) { setGrnPilih(null); setQty({}); return; }
    try {
      const { data } = await getGRN(gid);
      setGrnPilih(data);
      setQty({});
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  //: Baris yang benar-benar diretur = yang qty-nya diisi DAN > 0.
  //: Baris berisi 0 sengaja DIBUANG, bukan dikirim: backend menolak qty 0,
  //: dan mengirimnya membuat seluruh pengajuan gagal gara-gara baris yang
  //: sebenarnya nol diniatkan pengguna.
  const barisRetur = useMemo(() => Object.entries(qty)
    .map(([id, v]) => ({ po_receipt_item_id: id, qty_returned: Number(v) }))
    .filter(x => x.qty_returned > 0), [qty]);

  const jalankanBuat = async () => {
    if (!grnPilih || barisRetur.length === 0) return;
    if (alasanBuat.trim().length < MIN_ALASAN) return;
    setBusy(true);
    try {
      await createReturn({
        po_id: grnPilih.po_id,
        items: barisRetur,
        reason: alasanBuat.trim(),
      });
      toast.success("Retur dicatat — stok berkurang.");
      setBuatOpen(false);
      await muat();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const bolehBatal = meta?.permissions?.cancel;

  return (
    <div className="space-y-4" data-testid="page-vendor-returns">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Undo2 size={20} /> Retur Vendor
          </h1>
          <p className="text-sm text-muted-foreground">
            Barang yang dikembalikan ke vendor. Retur <strong>mengurangi
            stok</strong>; pembatalannya mengembalikan stok itu.
          </p>
        </div>
        <div className="flex gap-2">
        {meta?.permissions?.create && (
          <Button onClick={bukaBuat} data-testid="ret-new">
            <Plus size={16} /> Catat retur
          </Button>
        )}
        <Button variant="outline" onClick={muat} disabled={loading}
                data-testid="ret-refresh">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Muat ulang
        </Button>
        </div>
      </div>

      {/* K214 / BUSINESS-87: sisa uang kembali retur cara refund — ditagih Admin Purchasing. */}
      {SISA_UANG_KEMBALI_ROLES.includes(roleCode) && (
        <SisaUangKembaliPanel sumber="RETUR_VENDOR" testId="ret-sisa-uang-kembali" />
      )}
      {/* K215 / BUSINESS-90: uang lebih dari pemasok — dipilih Admin Purchasing, disetujui KC, dikembalikan Finance. */}
      {UANG_LEBIH_ROLES.includes(roleCode) && (
        <UangLebihPemasokPanel sumber="RETUR_VENDOR" testId="ret-uang-lebih" />
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari nomor retur, vendor, PO, atau alasan…" testId="ret-palang"
                    ekspor={{ nama: "retur_vendor", kolom: KOLOM_CSV, ambilHalaman }}>
        <select
          className="border rounded-md h-9 px-2 text-sm bg-background"
          value={daftar.nilaiSaringan.status}
          onChange={e => daftar.setSaringan("status", e.target.value)}
          aria-label="Saring status"
          data-testid="ret-status-filter"
        >
          <option value="">Semua status</option>
          {(meta?.statuses || []).map(s => (
            <option key={s.code} value={s.code}>{s.label}</option>
          ))}
        </select>
      </PalangDaftar>

      {err && (
        <Card className="p-4 flex gap-2 items-start text-destructive"
              data-testid="ret-error">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <span>{err}</span>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <ThUrut daftar={daftar} kunci="return_number" className="p-2">Nomor</ThUrut>
              <ThUrut daftar={daftar} kunci="return_date" className="p-2">Tanggal</ThUrut>
              <th className="p-2">Vendor</th>
              <th className="p-2">PO</th>
              <th className="p-2">Alasan</th>
              <ThUrut daftar={daftar} kunci="status" className="p-2">Status</ThUrut>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && daftar.tampil.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                Memuat…
              </td></tr>
            )}
            {!loading && daftar.tampil.length === 0 && (
              // ⚠ Kosong DIBEDAKAN dari "belum pernah ada": staf yang menyaring
              // terlalu sempit lalu melihat "belum ada retur" akan menyimpulkan
              // datanya hilang.
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                <PackageX size={28} className="mx-auto mb-2 opacity-40" />
                {!daftar.cari.trim() && daftar.saringanAktif.length === 0
                  ? "Belum ada retur vendor."
                  : "Tidak ada retur yang cocok dengan penyaring ini."}
              </td></tr>
            )}
            {daftar.tampil.map(r => {
              const b = statusBadge(meta, r.status);
              return (
                <tr key={r.id} className="border-t hover:bg-muted/30"
                    data-testid={`ret-row-${r.return_number}`}>
                  <td className="p-2 font-medium">{r.return_number}</td>
                  <td className="p-2">{formatDate(r.return_date)}</td>
                  <td className="p-2">{r.vendor_name || "—"}</td>
                  <td className="p-2">{r.po_number || "—"}</td>
                  <td className="p-2 max-w-[18rem] truncate" title={r.reason}>
                    {r.reason || "—"}
                  </td>
                  <td className="p-2">
                    <StatusBadge tone={b.tone} title={b.description}>
                      {b.label}
                    </StatusBadge>
                  </td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost"
                            onClick={() => bukaDetail(r.id)}
                            data-testid={`ret-detail-${r.return_number}`}>
                      Rincian
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="ret-paginasi" />
      </Card>

      {/* ---------------- Rincian ---------------- */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Retur {detail?.return_number}</DialogTitle>
            <DialogDescription>
              {statusBadge(meta, detail?.status).description}
            </DialogDescription>
          </DialogHeader>

          {detailLoading && <p className="text-muted-foreground">Memuat…</p>}

          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Vendor</span><br />
                  {detail.vendor_name || "—"}</div>
                <div><span className="text-muted-foreground">PO</span><br />
                  {detail.po_number || "—"}</div>
                <div><span className="text-muted-foreground">Tanggal</span><br />
                  {formatDate(detail.return_date)}</div>
                <div><span className="text-muted-foreground">Status</span><br />
                  <StatusBadge tone={statusBadge(meta, detail.status).tone}>
                    {statusBadge(meta, detail.status).label}
                  </StatusBadge>
                </div>
              </div>

              <div>
                <span className="text-muted-foreground">Alasan retur</span>
                <p>{detail.reason || "—"}</p>
              </div>

              {/* Alasan pembatalan hanya ada kalau memang dibatalkan — dan ia
                  WAJIB tampil: pembatalan mengembalikan stok, jadi sebabnya
                  adalah bagian dari jejak stok, bukan catatan sampingan. */}
              {detail.cancel_reason && (
                <div className="rounded-md border border-neutral-soft-border bg-neutral-soft p-2">
                  <span className="text-muted-foreground">Alasan pembatalan</span>
                  <p>{detail.cancel_reason}</p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-t">
                  <thead className="bg-muted/40">
                    <tr className="text-left">
                      <th className="p-2">Barang</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2">Satuan</th>
                      <th className="p-2 text-right">Harga</th>
                      <th className="p-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.items || []).map(it => (
                      <tr key={it.id} className="border-t">
                        {/* Nama bahan dari backend (rantai receipt→po_item→master). Nol pernah
                            jatuh ke UUID: UUID "benar untuk mesin, buta untuk orang". */}
                        <td className="p-2">{it.material_name ? `${it.material_code ? it.material_code + " · " : ""}${it.material_name}` : "— (nama tidak terkirim)"}</td>
                        <td className="p-2 text-right">{it.qty_returned}</td>
                        <td className="p-2">{it.unit_of_measure || "—"}</td>
                        <td className="p-2 text-right">{formatIDR(it.unit_price)}</td>
                        <td className="p-2 text-right">{formatIDR(it.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PenyelesaianRetur retur={detail} onBerubah={() => { bukaDetail(detail.id); muat(); }} />
            </div>
          )}

          <DialogFooter>
            {/* Tombol batal hanya untuk retur yang masih RETURNED, dan hanya
                bagi yang backend izinkan. `permissions` DIKIRIM backend —
                menebaknya dari nama peran akan menampilkan tombol yang
                backend tolak. */}
            {detail?.status === "RETURNED" && bolehBatal && (
              <Button variant="destructive"
                      onClick={() => { setBatalFor(detail); setAlasan(""); }}
                      data-testid="ret-cancel-btn">
                Batalkan retur
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Catat retur ---------------- */}
      <Dialog open={buatOpen} onOpenChange={o => !o && setBuatOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Catat retur vendor</DialogTitle>
            <DialogDescription>
              Pilih penerimaan barangnya dulu, lalu isi jumlah yang
              dikembalikan. <strong>Stok akan berkurang</strong> begitu retur
              tercatat.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div>
              <label className="text-muted-foreground">Penerimaan barang</label>
              {/* ⛔ Dulu <select> berisi 50 penerimaan pertama — penerimaan ke-51 dst.
                  tidak bisa diretur dari layar. Sekarang dicari di server. */}
              <div className="mt-1">
                <PemilihCari
                  value={grnPilih}
                  onChange={(g) => pilihGrn(g?.id || "")}
                  cari={cariGrn}
                  label={labelGrn}
                  keterangan={ketGrn}
                  tampilAwal
                  placeholder="— pilih penerimaan —"
                  placeholderCari="Ketik nomor penerimaan atau nomor PO…"
                  testId="ret-grn-select"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Hanya penerimaan berstatus Diterima yang bisa diretur.
              </p>
            </div>

            {grnPilih && (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="text-left">
                      <th className="p-2">Barang</th>
                      <th className="p-2 text-right">Diterima</th>
                      <th className="p-2">Satuan</th>
                      <th className="p-2 text-right w-32">Diretur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(grnPilih.items || []).map(it => (
                      <tr key={it.id} className="border-t">
                        <td className="p-2">
                          {it.po_item?.material?.name || it.po_item_id}
                        </td>
                        <td className="p-2 text-right">{it.qty_received}</td>
                        <td className="p-2">{it.unit_of_measure || "—"}</td>
                        <td className="p-2">
                          {/* `max` menahan salah ketik di layar, BUKAN
                              menggantikan pagar backend: batas sebenarnya
                              adalah AKUMULASI retur atas baris ini, dan
                              angka itu cuma backend yang tahu. */}
                          <Input
                            type="number" min="0" max={it.qty_received}
                            step="any"
                            className="h-8 text-right"
                            value={qty[it.id] ?? ""}
                            onChange={e => setQty(q => ({ ...q, [it.id]: e.target.value }))}
                            data-testid={`ret-qty-${it.id}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {grnPilih && (
              <Textarea
                placeholder={`Alasan retur (min. ${MIN_ALASAN} karakter)`}
                value={alasanBuat}
                onChange={e => setAlasanBuat(e.target.value)}
                data-testid="ret-new-reason"
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBuatOpen(false)}>
              Urung
            </Button>
            <BusyButton
              busy={busy}
              disabled={!grnPilih || barisRetur.length === 0
                        || alasanBuat.trim().length < MIN_ALASAN}
              onClick={jalankanBuat}
              data-testid="ret-new-confirm">
              Catat retur ({barisRetur.length} baris)
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Pembatalan ---------------- */}
      <Dialog open={!!batalFor} onOpenChange={o => !o && setBatalFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Batalkan retur {batalFor?.return_number}?</DialogTitle>
            <DialogDescription>
              Stok yang tadi berkurang akan <strong>dikembalikan</strong> lewat
              catatan stok pembalik. Jejaknya tetap tersimpan — pembatalan tidak
              menghapus riwayat.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            placeholder={`Alasan pembatalan (min. ${MIN_ALASAN} karakter)`}
            value={alasan}
            onChange={e => setAlasan(e.target.value)}
            data-testid="ret-cancel-reason"
          />
          {alasan.length > 0 && alasan.trim().length < MIN_ALASAN && (
            <p className="text-xs text-destructive">
              Alasan wajib diisi, minimal {MIN_ALASAN} karakter.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBatalFor(null)}>
              Urung
            </Button>
            <BusyButton variant="destructive" busy={busy}
                        disabled={alasan.trim().length < MIN_ALASAN}
                        onClick={jalankanBatal}
                        data-testid="ret-cancel-confirm">
              Batalkan retur
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
